import React from 'react';
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';

/**
 * Componente "Vantagens" do checkout (modelo Hotmart): selos de confiança
 * (Privacidade, Compra segura, Entrega via e-mail, Conteúdo aprovado) com
 * ícone na cor principal, título e uma linha de descrição.
 */

export type AdvantageKey = 'privacy' | 'secure' | 'email' | 'approved';

export interface AdvantagesConfig {
    theme?: 'light' | 'dark';
    /** Legado: a orientação agora é automática pelo espaço disponível. */
    orientation?: 'horizontal' | 'vertical';
    /** undefined = fundo do tema; 'none' = sem fundo; hex = cor escolhida. */
    backgroundColor?: string;
    privacy?: boolean;
    secure?: boolean;
    email?: boolean;
    approved?: boolean;
    mainColor?: string;
    titleColor?: string;
}

export const ADVANTAGES_DEFAULTS = {
    theme: 'light' as const,
    mainColor: '#1E88E5',
    titleColor: '#0D2A3A',
};

export const ADVANTAGES: { key: AdvantageKey; title: string; description: string; icon: string }[] = [
    {
        key: 'privacy',
        title: 'Privacidade',
        description: 'Seus dados pessoais ficam protegidos',
        // cadeado
        icon: 'M7 10V7a5 5 0 0 1 10 0v3M6 10h12a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1Zm6 4v3',
    },
    {
        key: 'secure',
        title: 'Compra segura',
        description: 'Ambiente protegido e criptografado',
        // escudo com check
        icon: 'M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3Zm-3 9 2 2 4-4',
    },
    {
        key: 'email',
        title: 'Entrega via E-mail',
        description: 'Você recebe o acesso no seu e-mail',
        // envelope
        icon: 'M4 6h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Zm0 1 8 6 8-6',
    },
    {
        key: 'approved',
        title: 'Conteúdo aprovado',
        description: 'Conteúdo revisado antes de ser publicado',
        // joinha
        icon: 'M7 11v9H4v-9h3Zm0 0 4-7a2 2 0 0 1 2 2v3h5a2 2 0 0 1 2 2.3l-1.2 7A2 2 0 0 1 16.8 20H7',
    },
];

/** Por padrão todas ligadas (quando a chave ainda não existe nas props). */
export const isAdvantageOn = (config: AdvantagesConfig, key: AdvantageKey) => config[key] !== false;

export const AdvantagesBlock: React.FC<{ config: AdvantagesConfig; placeholderWhenEmpty?: boolean }> = ({ config, placeholderWhenEmpty = false }) => {
    const dark = config.theme === 'dark';
    const mainColor = config.mainColor || ADVANTAGES_DEFAULTS.mainColor;
    const titleColor = dark ? '#FFFFFF' : config.titleColor || ADVANTAGES_DEFAULTS.titleColor;
    const items = ADVANTAGES.filter((a) => isAdvantageOn(config, a.key));
    const bg = config.backgroundColor;
    const noBg = bg === 'none';
    const customBg = !!bg && !noBg;

    if (items.length === 0) {
        if (!placeholderWhenEmpty) return null;
        return (
            <div className="w-full rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-400">
                Nenhuma vantagem ativa — ligue pelo menos uma no painel ao lado
            </div>
        );
    }

    // Orientação automática pelo espaço do container (não pela tela):
    // estreito (coluna lateral, celular) = uma por linha com ícone à esquerda;
    // largo (caixa de cima no desktop) = lado a lado, centralizadas.
    const cols =
        items.length === 1
            ? ''
            : items.length === 4
                ? '@md:grid-cols-2 @2xl:grid-cols-4'
                : items.length === 3
                    ? '@lg:grid-cols-3'
                    : '@md:grid-cols-2';
    const itemCls =
        items.length === 1
            ? 'flex items-center justify-center gap-3 text-left'
            : 'flex items-center gap-3 text-left @md:flex-col @md:gap-1.5 @md:text-center';

    const boxCls = noBg
        ? 'py-2'
        : customBg
            ? 'px-4 py-4'
            : `border px-4 py-4 ${dark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`;

    return (
        <div className={`@container w-full rounded-lg ${boxCls}`} style={customBg ? { backgroundColor: bg } : undefined}>
            <div className={`grid grid-cols-1 gap-4 ${cols}`}>
                {items.map((a) => (
                    <div key={a.key} className={itemCls}>
                        <span
                            className="flex items-center justify-center rounded-full shrink-0"
                            style={{ width: 40, height: 40, backgroundColor: `${mainColor}1A` }}
                        >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d={a.icon} stroke={mainColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                        <div className="min-w-0">
                            <p className="text-sm font-bold leading-tight" style={{ color: titleColor }}>{a.title}</p>
                            <p className={`text-xs leading-snug mt-0.5 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{a.description}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

interface CheckoutAdvantagesProps {
    selected: boolean;
    config: AdvantagesConfig;
    onSelect: () => void;
    onDelete?: () => void;
    onDuplicate?: () => void;
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
    viewMode?: boolean;
}

const CheckoutAdvantages: React.FC<CheckoutAdvantagesProps> = ({
    selected,
    config,
    onSelect,
    onDelete,
    onDuplicate,
    dragHandleProps,
    viewMode = false,
}) => {
    if (viewMode) {
        return <AdvantagesBlock config={config} />;
    }

    return (
        <div
            className={`relative group w-full cursor-pointer rounded-lg ${selected ? 'ring-2 ring-blue-500' : 'hover:ring-2 hover:ring-blue-300'}`}
            onClick={onSelect}
        >
            <AdvantagesBlock config={config} placeholderWhenEmpty />

            {selected && (
                <div className="absolute top-2 right-2 flex gap-1 z-10">
                    <span
                        {...dragHandleProps}
                        className="p-1.5 rounded bg-white shadow-lg border text-gray-600 hover:text-gray-800 cursor-grab active:cursor-grabbing"
                        title="Arrastar"
                    >
                        <i className="ri-drag-move-2-line text-sm"></i>
                    </span>
                    {onDuplicate && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
                            className="p-1.5 rounded bg-white shadow-lg border text-gray-600 hover:text-gray-800"
                            title="Duplicar"
                        >
                            <i className="ri-file-copy-line text-sm"></i>
                        </button>
                    )}
                    {onDelete && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDelete(); }}
                            className="p-1.5 rounded bg-white shadow-lg border text-red-600 hover:text-red-800"
                            title="Excluir"
                        >
                            <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default CheckoutAdvantages;
